import { nanoid } from '@reduxjs/toolkit'
import { call, put, takeLatest } from 'redux-saga/effects'

import { NotificationManager } from '@/components/Notification'
import { adminEmployeeService, adminFapiUserManagement } from '@/services/admin'
import { STATUS_CODE } from '@/libs/constants'
import { RESPONSE_STATUS } from '@/libs/constants/statuses'
import { USER_TYPES } from '@/libs/constants/user'
import { getErrorData } from '@/libs/utils/errorUtils'
import { linkGenerator } from '@/libs/utils/language'

import {
  addEmployeeUser,
  addEmployeeUserFailed,
  addEmployeeUserSuccess,
  getEmployeeAvailableLanguages,
  getEmployeeAvailableLanguagesFailed,
  getEmployeeAvailableLanguagesSuccess,
  getSuggestionList,
  getSuggestionListFailed,
  getSuggestionListSuccess,
  getUserOverviewList,
  getUserOverviewListFailed,
  getUserOverviewListSuccess,
} from '../slices/usersManagementSlice'

function* getUserOverviewListSaga({ payload }) {
  try {
    const { data } = yield adminFapiUserManagement.getUserList(payload)
    const { items } = data

    // Add key to each user to prevent empty users
    // that leads to warning of duplicated row key in table
    const transformedItems = items.map((item) => ({
      ...item,
      key: nanoid(),
      sapCustomerNumber: item?.userType === 'T' ? item?.customerNumber : item?.sapCustomerNumber,
    }))

    const transformedData = { ...data, items: transformedItems }

    yield put(getUserOverviewListSuccess(transformedData))
  } catch (error) {
    yield put(getUserOverviewListFailed())
    NotificationManager.error({
      message: 'notification.error.getUserList',
      description: getErrorData(error),
    })
  }
}

function* getEmployeeSuggestionListSaga({ payload }) {
  try {
    const { data, status } = yield adminEmployeeService.getEmployeeSuggestionList(payload)
    if (status === STATUS_CODE.SUCCESS) {
      yield put(getSuggestionListSuccess(data))
    }
  } catch (error) {
    yield put(getSuggestionListFailed(getErrorData(error)))
  }
}

function* getEmployeeAvailableLanguagesSaga({ payload }) {
  try {
    const { data } = yield call(
      adminEmployeeService.getEmployeeUserAvailableLanguages,
      payload,
    )
    const formattedData = JSON.parse(data)

    yield put(getEmployeeAvailableLanguagesSuccess(formattedData))
  } catch (error) {
    yield put(getEmployeeAvailableLanguagesFailed(getErrorData(error)))
  }
}

function* addEmployeeUserSaga({ payload }) {
  try {
    const { navigate, ...employeeUserData } = payload
    const { data, status } = yield call(
      adminEmployeeService.addEmployeeUser,
      employeeUserData,
    )

    if (status === STATUS_CODE.SUCCESS) {
      yield put(addEmployeeUserSuccess(data))
      NotificationManager.success({
        message: 'notification.success.addEmployeeUser',
      })
      navigate(linkGenerator(`/user-management/${USER_TYPES.EMPLOYEE_USER}`))
    }
  } catch (error) {
    const { errorCode, detail } = getErrorData(error) || {}
    const parsedDetail = JSON.parse(detail)

    if (
      errorCode === RESPONSE_STATUS.duplicateCode
      || parsedDetail?.message?.includes('exists')
    ) {
      NotificationManager.error({
        message: 'notification.error.addEmployeeUser',
        description: 'notification.error.userAlreadyExists',
      })
    } else {
      NotificationManager.error({
        message: 'notification.error.addEmployeeUser',
      })
    }

    yield put(addEmployeeUserFailed())
  }
}

export default function* userSaga() {
  yield takeLatest(getSuggestionList.type, getEmployeeSuggestionListSaga)
  yield takeLatest(
    getEmployeeAvailableLanguages.type,
    getEmployeeAvailableLanguagesSaga,
  )
  yield takeLatest(addEmployeeUser.type, addEmployeeUserSaga)
  yield takeLatest(getUserOverviewList.type, getUserOverviewListSaga)
}
